import { useQuery } from "@tanstack/react-query";
import { Outlet, getRouteApi, useNavigate, useParams } from "@tanstack/react-router";
import { listBatches } from "@/api/client";
import { BatchTable } from "@/components/batch";
import { PagedFooter } from "@/components/Pagination";
import { titleStatus } from "@/components/format";
import { EmptyState, ErrorBanner, StatusDot, WorkspaceHeader } from "@/components/ui";
import { pageParams } from "@/routes/paging";

export const MONITOR_STATUSES = ["all", "pending_approval", "approved", "generating", "generated", "distributing", "completed", "failed", "rejected"] as const;
export type MonitorStatus = (typeof MONITOR_STATUSES)[number];
export const DEFAULT_MONITOR_STATUS: MonitorStatus = "generating";

const batchesApi = getRouteApi("/staff/batches");

export function BatchMonitor() {
  const search = batchesApi.useSearch();
  const { status: requested, page, page_size } = search;
  const status = requested ?? DEFAULT_MONITOR_STATUS;
  const activeID = useParams({ strict: false }).batchId ?? null;
  const navigate = useNavigate();

  const batches = useQuery({
    queryKey: ["batches", status, page, page_size],
    queryFn: () => listBatches({ status: status === "all" ? undefined : status, ...pageParams(search) }),
    refetchInterval: 10_000,
  });
  const items = batches.data?.items ?? [];

  return (
    <section className="space-y-5">
      <WorkspaceHeader
        title="Batch monitor"
        detail="Generation status, failure reports, and retry control"
        action={<StatusDot label={batches.isFetching ? "Refreshing" : "Live"} />}
      />

      <ErrorBanner error={batches.error} />

      <div className="grid gap-5 xl:grid-cols-[minmax(0,1.15fr)_minmax(380px,0.85fr)]">
        <div className="overflow-hidden rounded-lg border border-zinc-200 bg-white">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-zinc-200 px-4 py-3">
            <div className="text-sm font-medium">Batches</div>
            <select
              aria-label="Status filter"
              value={status}
              onChange={(e) => void navigate({ to: "/batches", search: { status: e.target.value as MonitorStatus, page_size } })}
              className="rounded-md border border-zinc-300 bg-white px-3 py-2 text-sm outline-none focus:border-brand focus:ring-1 focus:ring-brand"
            >
              {MONITOR_STATUSES.map((value) => (
                <option key={value} value={value}>
                  {value === "all" ? "All statuses" : titleStatus(value)}
                </option>
              ))}
            </select>
          </div>
          {items.length === 0 ? (
            <EmptyState title="No batches in this view" detail="Change the status filter to inspect another queue." />
          ) : (
            <BatchTable
              batches={items}
              activeID={activeID}
              onActive={(batchId) =>
                // Keep the filter exactly as the URL has it, including "not set".
                void navigate({ to: "/batches/$batchId", params: { batchId }, search: { status: requested, page, page_size } })
              }
            />
          )}
          <PagedFooter data={batches.data} page={page} pageSize={page_size} />
        </div>

        <aside className="rounded-lg border border-zinc-200 bg-white p-4">
          <Outlet />
        </aside>
      </div>
    </section>
  );
}
